import React from "react";
import { useState } from "react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import Logo from "../../../images/logo.png";
import UserImage from "../../../images/icon/user-icon.svg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ConnectButton } from "@rainbow-me/rainbowkit";
import { useDispatch, useSelector } from "react-redux";
import { user_logout_thunk } from "../../../redux/thunk/user_thunk";
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Avatar,
  Button,
  useDisclosure
} from "@heroui/react";
import { useTransakWidget } from "../../../hooks/useTransak";
import NotificationComponent from "../../common-layout/notification-component";
import { useWallet } from "../../../context/WalletContext";
import { formatWalletAddress } from "../../../utils/common";
import WalletConnectModal from "../wallet-connect-modal";
import AccountInfoModal from "../account-info-modal";

export default function Header() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const user_address = user?.data?.address;
  const userBalance = useSelector((state) => state?.user?.balance);
  const navigate = useNavigate();

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const {
    address,
    isConnected,
    connectWallet,
    bafBalanceFormatted,
    bafBalanceSymbol,
    ensAvatar,
    disconnectWallet
  } = useWallet();
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();
  const {
    isOpen: initialIsWalletOpen,
    onOpen: initialWalletOpen,
    onClose: initialWalletClose,
    onOpenChange: initialWalletChange
  } = useDisclosure();
  const location = useLocation();

  const handleLogin = () => {
    navigate("/login");
  };

  const handleSignUp = () => {
    navigate("/sign-up");
  };

  const handleLogout = () => {
    dispatch(user_logout_thunk(disconnectWallet));
  };

  const { openTransakWidget } = useTransakWidget({
    walletAddress: address ? address : "",
    defaultCryptoCurrency: "USDT"
  });

  const handleBuyToken = () => {
    if (isConnected) {
      openTransakWidget();
    } else {
      connectWallet();
    }
  };

  const handleDisconnectWallet = () => {
    disconnectWallet();
    onClose();
  };

  const modalLogout = () => {
    if (user?.data?.is_custodial_wallet) {
      handleLogout();
    } else {
      handleDisconnectWallet();
    }
    onClose();
  };

  const avatarUrl =
    ensAvatar || `https://api.dicebear.com/9.x/bottts/svg?seed=${address}`;

  return (
    <>
      <header className="sticky top-0 z-50 bg-black">
        <nav
          aria-label="Global"
          className="flex items-center justify-between p-4 lg:px-16"
        >
          <div className="flex">
            <Link to="/" className="-m-1.5 p-1.5">
              <span className="sr-only">BAF</span>
              <img
                alt="Logo"
                src={Logo}
                className="h-auto w-[6rem] sm:w-[8rem]"
              />
            </Link>
          </div>

          <div className="hidden xl:flex lg:gap-x-12">
            <Link
              to="/"
              className={`text-base font-medium ${
                location?.pathname === "/" ? "text-yellowbuy" : "text-white"
              }`}
            >
              Home
            </Link>
            <Link
              to="/staking"
              className={`text-base font-medium ${
                location?.pathname === "/staking"
                  ? "text-yellowbuy"
                  : "text-white"
              }`}
            >
              Staking
            </Link>
            <Link
              to="/token-swap"
              className={`text-base font-medium ${
                location?.pathname === "/token-swap"
                  ? "text-yellowbuy"
                  : "text-white"
              }`}
            >
              Token Swap
            </Link>
            <Link
              to="/dao"
              className={`text-base font-medium ${
                location?.pathname === "/dao" ? "text-yellowbuy" : "text-white"
              }`}
            >
              DAO
            </Link>
          </div>
          <div className="flex items-center gap-4 justify-end">
            <div className="flex items-center gap-2 xs:gap-4 justify-end">
              {user?.login ? (
                <div className="flex items-center gap-2 xs:gap-4">
                  <div className="hidden lg:block connect-button-wrapper">
                    {user?.data?.is_custodial_wallet ? (
                      <Button
                        className="bg-yellowbuy text-black text-base font-normal text-center min-w-[9.5rem] min-h-[2.5rem]"
                        onPress={onOpen}
                      >
                        {formatWalletAddress(user?.data?.address)} &#40;
                        {userBalance} BAF&#41;
                      </Button>
                    ) : (
                      <ConnectButton.Custom>
                        {({
                          account,
                          chain,
                          openChainModal,
                          openConnectModal,
                          authenticationStatus,
                          mounted
                        }) => {
                          const ready =
                            mounted && authenticationStatus !== "loading";
                          const connected =
                            ready &&
                            account &&
                            chain &&
                            (!authenticationStatus ||
                              authenticationStatus === "authenticated");
                          return (
                            <div
                              {...(!ready && {
                                "aria-hidden": true,
                                style: {
                                  opacity: 0,
                                  pointerEvents: "none",
                                  userSelect: "none"
                                }
                              })}
                            >
                              {(() => {
                                if (!connected) {
                                  return (
                                    <Button
                                      className="bg-yellowbuy text-black text-base font-normal text-center min-w-[9.5rem] min-h-[2.5rem]"
                                      onPress={
                                        user_address
                                          ? openConnectModal
                                          : initialWalletOpen
                                      }
                                    >
                                      Connect Wallet
                                    </Button>
                                  );
                                }
                                if (chain.unsupported) {
                                  return (
                                    <Button
                                      className="bg-yellowbuy text-black text-base font-normal text-center min-w-[9.5rem] min-h-[2.5rem]"
                                      onPress={openChainModal}
                                    >
                                      Wrong network
                                    </Button>
                                  );
                                }
                                return (
                                  <Button
                                    className="bg-yellowbuy text-black text-base font-normal text-center min-w-[9.5rem] min-h-[2.5rem] flex gap-2 items-center"
                                    onPress={onOpen}
                                    type="button"
                                  >
                                    {account.displayName}
                                    <span>
                                      &#40;
                                      {bafBalanceFormatted
                                        ? Number(bafBalanceFormatted)?.toFixed(
                                            2
                                          )
                                        : "0.00"}{" "}
                                      {/* {bafBalanceSymbol || "BAF"} */}
                                      {"BAF"}
                                      &#41;
                                    </span>
                                  </Button>
                                );
                              })()}
                            </div>
                          );
                        }}
                      </ConnectButton.Custom>
                    )}
                  </div>

                  <Dropdown placement="bottom-end">
                    <DropdownTrigger className="!w-10 !h-10 rounded-lg bg-yellowbuy ring-0 ring-offset-0 ring-offset-none">
                      <Avatar
                        isBordered
                        as="button"
                        classNames={{ img: "!w-6 !h-6" }}
                        src={UserImage}
                      />
                    </DropdownTrigger>
                    <DropdownMenu aria-label="Profile Actions" variant="flat">
                      <DropdownItem
                        key="profile"
                        onPress={() => navigate("/profile?tab=collectable")}
                      >
                        Profile
                      </DropdownItem>
                      <DropdownItem key="buy_token" onPress={handleBuyToken}>
                        Buy Token
                      </DropdownItem>
                      <DropdownItem
                        key="logout"
                        color="danger"
                        onPress={handleLogout}
                      >
                        Log Out
                      </DropdownItem>
                    </DropdownMenu>
                  </Dropdown>

                  <NotificationComponent />
                </div>
              ) : (
                <div className="hidden lg:flex items-center gap-4">
                  <Button
                    onPress={() => handleSignUp()}
                    radius="sm"
                    className="bg-yellowbuy border border-black text-black"
                  >
                    Sign Up
                  </Button>
                  <Button
                    onPress={() => handleLogin()}
                    radius="sm"
                    className="bg-yellowbuy border border-black text-black"
                  >
                    Log in
                  </Button>
                </div>
              )}

              <Button
                onPress={() => setMobileMenuOpen(true)}
                className="flex xl:hidden items-center justify-center p-2 min-w-10 max-w-10 text-black rounded-lg bg-yellowbuy border border-black"
              >
                <Bars3Icon aria-hidden="true" className="w-6 h-6" />
              </Button>
            </div>
          </div>
        </nav>

        {/* Mobile Menu */}
        <div
          className={`fixed inset-0 z-[10] bg-black bg-opacity-30 transition-opacity duration-300 ${
            mobileMenuOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
        >
          <div
            className={`fixed inset-y-0 right-0 z-20 w-full max-w-sm overflow-y-auto bg-black p-4 transform transition-transform duration-300 ease-in-out ${
              mobileMenuOpen ? "translate-x-0" : "translate-x-full"
            }`}
          >
            <div className="flex items-center justify-between">
              <Link to="/" className="-m-1.5 p-1.5">
                <span className="sr-only">BAF</span>
                <img
                  alt="Logo"
                  src={Logo}
                  className="h-auto w-[6rem] sm:w-[8rem]"
                />
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="-m-2.5 rounded-md p-2.5 text-gray-700"
              >
                <span className="sr-only">Close menu</span>
                <XMarkIcon aria-hidden="true" className="w-6 h-6 text-white" />
              </button>
            </div>
            <div className="mt-6 flow-root">
              <div className="flex flex-col gap-4">
                <Link
                  to="/"
                  className={`block rounded-lg px-3 py-2 text-base font-semibold ${
                    location?.pathname === "/" ? "text-yellowbuy" : "text-white"
                  } hover:bg-neutral-600`}
                >
                  Home
                </Link>
                <Link
                  to="/staking"
                  className={`block rounded-lg px-3 py-2 text-base font-semibold ${
                    location?.pathname === "/staking"
                      ? "text-yellowbuy"
                      : "text-white"
                  } hover:bg-neutral-600`}
                >
                  Staking
                </Link>
                <Link
                  to="/token-swap"
                  className={`block rounded-lg px-3 py-2 text-base font-semibold ${
                    location?.pathname === "/token-swap"
                      ? "text-yellowbuy"
                      : "text-white"
                  } hover:bg-neutral-600`}
                >
                  Token Swap
                </Link>
                <Link
                  to="/dao"
                  className={`block rounded-lg px-3 py-2 text-base font-semibold ${
                    location?.pathname === "/dao"
                      ? "text-yellowbuy"
                      : "text-white"
                  } hover:bg-neutral-600`}
                >
                  DAO
                </Link>
                <div className="flex flex-col gap-4 px-3">
                  {user?.login ? (
                    user?.data?.is_custodial_wallet ? (
                      <Button
                        className="w-full bg-yellowbuy text-black text-base font-normal text-center min-w-[9.5rem] min-h-[2.5rem]"
                        onPress={onOpen}
                      >
                        {formatWalletAddress(user?.data?.address)} &#40;
                        {userBalance} BAF&#41;
                      </Button>
                    ) : (
                      <ConnectButton.Custom>
                        {({
                          account,
                          chain,
                          openChainModal,
                          openConnectModal,
                          authenticationStatus,
                          mounted
                        }) => {
                          const ready =
                            mounted && authenticationStatus !== "loading";
                          const connected =
                            ready &&
                            account &&
                            chain &&
                            (!authenticationStatus ||
                              authenticationStatus === "authenticated");
                          return (
                            <div
                              {...(!ready && {
                                "aria-hidden": true,
                                style: {
                                  opacity: 0,
                                  pointerEvents: "none",
                                  userSelect: "none"
                                }
                              })}
                            >
                              {(() => {
                                if (!connected) {
                                  return (
                                    <Button
                                      className="w-full bg-yellowbuy text-black text-base font-normal text-center min-w-[9.5rem] min-h-[2.5rem]"
                                      onPress={openConnectModal}
                                    >
                                      Connect Wallet
                                    </Button>
                                  );
                                }
                                if (chain.unsupported) {
                                  return (
                                    <Button
                                      className="w-full bg-yellowbuy text-black text-base font-normal text-center min-w-[9.5rem] min-h-[2.5rem]"
                                      onPress={openChainModal}
                                    >
                                      Wrong network
                                    </Button>
                                  );
                                }
                                return (
                                  <Button
                                    className="w-full bg-yellowbuy text-black text-base font-normal text-center min-w-[9.5rem] min-h-[2.5rem] flex gap-2 items-center"
                                    onPress={onOpen}
                                    type="button"
                                  >
                                    {account.displayName}
                                    <span>
                                      &#40;
                                      {bafBalanceFormatted
                                        ? Number(bafBalanceFormatted)?.toFixed(
                                            2
                                          )
                                        : "0.00"}{" "}
                                      {bafBalanceSymbol || "BAF"}
                                      &#41;
                                    </span>
                                  </Button>
                                );
                              })()}
                            </div>
                          );
                        }}
                      </ConnectButton.Custom>
                    )
                  ) : (
                    <>
                      <Button
                        onPress={() => handleSignUp()}
                        radius="sm"
                        className="bg-yellowbuy border border-black text-black"
                      >
                        Sign Up
                      </Button>
                      <Button
                        onPress={() => handleLogin()}
                        radius="sm"
                        className="bg-yellowbuy border border-black text-black"
                      >
                        Log in
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {isOpen && (
        <AccountInfoModal
          isOpen={isOpen}
          onOpenChange={onOpenChange}
          bafBalanceFormatted={
            user?.data?.is_custodial_wallet ? userBalance : bafBalanceFormatted
          }
          address={address}
          bafBalanceSymbol="BAF"
          avatarUrl={avatarUrl}
          modalLogout={modalLogout}
          walletType={user?.data?.is_custodial_wallet}
        />
      )}
      {initialIsWalletOpen && (
        <WalletConnectModal
          initialIsWalletOpen={initialIsWalletOpen}
          initialWalletChange={initialWalletChange}
          onClose={initialWalletClose}
        />
      )}
    </>
  );
}
